import { Button, Form, Input } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getVehForLocation } from '../../offices/offices-selectors';
import { capitalize } from 'lodash';
import { createLocation } from '../../offices/offices-actions';
import { vehConfig } from '../configs/veh-config';
import { getAdminEmails, getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { formItemMap } from '@med-fe/ui';
import moment from 'moment';
import { useMsal } from '@azure/msal-react';
import { fetchAdminEmails } from 'apps/mod/src/store/user/user-actions';

function VehDetails({ locId }) {
  const [form] = Form.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useDispatch();
  const data = useSelector((state) => getVehForLocation(state, locId));
  const isEditAllowed = useSelector(getWriteTabsPermissions).veh;
  const { accounts } = useMsal();
  const adminEmails = useSelector(getAdminEmails);
  const [allowDateClear, setAllowDateClear] = useState<boolean>(false);
  const hasMounted = useRef(false);
  const [vehGoLive, setVehGoLive] = useState(data?.vehGoLive ? data?.vehGoLive : null);
  const [vehLastDay, setVehLastDay] = useState(data?.vehLastDay ? data?.vehLastDay : null);
  const [slitLampEquipment, setSlitLampEquipment] = useState(data?.slitLampEquipment || null);
  const [preTestEquipment, setPreTestEquipment] = useState(data?.preTestEquipment || null);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchAdminEmails());
  }, []);

  useEffect(() => {
    setAllowDateClear(adminEmails?.some((user) => user?.trim().toLowerCase() === accounts[0]?.username));
  }, [adminEmails, accounts]);

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      ...(!!data.vehLastDay && { vehLastDay: moment(data.vehLastDay) }),
      ...(!!data.vehGoLive && { vehGoLive: moment(data.vehGoLive) }),
    });
    setSlitLampEquipment(data?.slitLampEquipment || null);
    setPreTestEquipment(data?.preTestEquipment || null);
  }, [locId]);

  // Set "Other" field values after the parent state has been updated
  useEffect(() => {
    if (slitLampEquipment === 'Other' && data?.slitLampEquipmentOther) {
      form.setFieldsValue({ slitLampEquipmentOther: data.slitLampEquipmentOther });
    }
  }, [slitLampEquipment]);

  useEffect(() => {
    if (preTestEquipment === 'Other' && data?.preTestEquipmentOther) {
      form.setFieldsValue({ preTestEquipmentOther: data.preTestEquipmentOther });
    }
  }, [preTestEquipment]);

  const onSubmit = (formData: any) => {
    const { teleOptometry, ...formDataValues } = formData;
    const payload = {
      locationVeh: {
        ...formDataValues,
        ...(!!formDataValues.vehLastDay && { vehLastDay: vehLastDay }),
        ...(!!formDataValues.vehGoLive && { vehGoLive: vehGoLive }),
        id: data.vehId,
      },
      id: locId,
      teleOptometry: teleOptometry,
    };
    dispatch(createLocation(payload));
  };

  const onVehGoLiveSelect = (value) => {
    form.setFieldsValue({ vehGoLive: value ? value.format('YYYY-MM-DD') : null });
    setVehGoLive(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onVehLastDaySelect = (value) => {
    form.setFieldsValue({ vehLastDay: value ? value.format('YYYY-MM-DD') : null });
    setVehLastDay(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className='pro-service-details-form'
      onFinish={(formData) => {
        onSubmit(formData);
        setIsDisabled(true);
      }}
      onValuesChange={() => setIsDisabled(false)}
    >
      {Object.entries(vehConfig).map(([key, value]: any) => {
        // Handle conditional visibility for "Other" fields
        if (value.showWhen) {
          const dependentValue = value.showWhen.field === 'slitLampEquipment' ? slitLampEquipment : preTestEquipment;
          if (dependentValue !== value.showWhen.value) {
            return null;
          }
        }
        const params = {
          key,
          label: capitalize(value.label),
          name: key,
          rules: value.rules,
          data: value.data,
          ...(value.type === 'checkbox' && { valuePropName: 'checked' }),
        };
        const formItemParams = {
          ...value,
          allowDateClear: value.allowClear || allowDateClear,
          ...(key === 'vehGoLive' && {
            onSelect: onVehGoLiveSelect,
            data: vehGoLive,
          }),
          ...(key === 'vehLastDay' && {
            onSelect: onVehLastDaySelect,
            data: vehLastDay,
          }),
          ...(key === 'slitLampEquipment' && {
            onSelect: (val) => {
              setSlitLampEquipment(val);
              if (val !== 'Other') {
                form.setFieldsValue({ slitLampEquipmentOther: undefined });
              }
              setIsDisabled(false);
            },
          }),
          ...(key === 'preTestEquipment' && {
            onSelect: (val) => {
              setPreTestEquipment(val);
              if (val !== 'Other') {
                form.setFieldsValue({ preTestEquipmentOther: undefined });
              }
              setIsDisabled(false);
            },
          }),
        };
        // Render input for "Other" text fields
        if (value.type === 'input') {
          return (
            <Form.Item {...params}>
              <Input disabled={!isEditAllowed} />
            </Form.Item>
          );
        }
        return <Form.Item {...params}>{formItemMap(formItemParams, !isEditAllowed)}</Form.Item>;
      })}

      <Form.Item>
        <Button type='primary' htmlType='submit' className={'btn-form-action'} disabled={!isEditAllowed || isDisabled}>
          SAVE
        </Button>
      </Form.Item>
    </Form>
  );
}

export default VehDetails;
